import { useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import { loadRoundSnapshot } from "../api/round-api.js";
import { roundJournal } from "../data/local-database.js";
import { SeasonRoundStartStore } from "../sync/season-round-start-store.js";
import { executeReviewedImport } from "../pages/tee-off/ggb/ggb-import-executor.js";
import type { ReviewedImportInput } from "../pages/tee-off/ggb/ggb-round.js";
import type { ActionContext } from "./round-workspace-sync.js";
import { importValue } from "../pages/tee-off/ggb/ggb-import-value.js";
import {
  useServerImportWorkspace,
  type ServerImportWorkspaceActions,
} from "./round-workspace-import-server.js";

export interface ImportWorkspaceActions extends ServerImportWorkspaceActions {
  importReviewedRound(input: ReviewedImportInput): Promise<boolean>;
}

export function useImportWorkspace(
  context: ActionContext,
  setBusy: Dispatch<SetStateAction<boolean>>,
): ImportWorkspaceActions {
  const serverImport = useServerImportWorkspace(context, setBusy);
  const latest = useRef(context);
  latest.current = context;
  const pending = useRef<{
    accountId: string;
    key: string;
    job: Promise<boolean>;
  } | null>(null);
  useEffect(() => {
    pending.current = null;
    setBusy(false);
  }, [context.accountId, setBusy]);
  return {
    ...serverImport,
    importReviewedRound: (input) => {
      const key = importValue(input);
      if (pending.current) {
        if (
          pending.current.accountId === context.accountId &&
          pending.current.key === key
        )
          return pending.current.job;
        context.setMessage("Another import is already running.");
        return Promise.resolve(false);
      }
      const job = runImport(
        context,
        () => latest.current,
        setBusy,
        structuredClone(input),
      );
      pending.current = { accountId: context.accountId, key, job };
      const clear = () => {
        if (pending.current?.job === job) pending.current = null;
      };
      void job.then(clear, clear);
      return job;
    },
  };
}

async function runImport(
  context: ActionContext,
  current: () => ActionContext,
  setBusy: Dispatch<SetStateAction<boolean>>,
  input: ReviewedImportInput,
): Promise<boolean> {
  const access = () => {
    const next = current();
    if (
      next.accountId !== context.accountId ||
      !next.authenticated ||
      !next.online
    )
      throw new Error(
        "Connect and sign in to the original account to continue this import.",
      );
    if (new SeasonRoundStartStore(localStorage).pending(context.accountId))
      throw new Error(
        "Continue or abandon the season setup before importing. Imported rounds cannot be added to a season after play.",
      );
    if (next.conflicts.length)
      throw new Error("Resolve the open score conflict before importing.");
  };
  setBusy(true);
  try {
    access();
    await context.autoSync.settle();
    const snapshot = await executeReviewedImport({
      accountId: context.accountId,
      engine: context.engine,
      journal: roundJournal,
      input,
      access,
      readConfirmedRound: loadRoundSnapshot,
    });
    access();
    context.setSnapshot(snapshot);
    context.setMessage("Imported round confirmed on the server.");
    return true;
  } catch (error) {
    if (current().accountId === context.accountId)
      context.setMessage(
        error instanceof Error ? error.message : "The import could not finish.",
      );
    return false;
  } finally {
    const snapshot = await roundJournal.getSnapshot(
      context.accountId,
      input.roundId,
    );
    const queued = await roundJournal.hasUnsynced(context.accountId);
    if (current().accountId === context.accountId) {
      if (snapshot) context.setSnapshot(snapshot);
      context.setQueued(queued);
      const conflicts = context.engine.getConflicts();
      if (conflicts.length) {
        context.setConflicts(conflicts);
        context.setConflicted(true);
      }
      setBusy(false);
    }
  }
}
